import {
	type CSSProperties,
	type MutableRefObject,
	type ReactNode,
	useEffect,
	useRef
} from 'react'

import { type InfiniteLoadingReturn } from '../../hooks';
import { useEffectEvent } from '../../hooks/useEffectEvent';

type RefreshControlProps<
	Data extends any[],
	FilterSearchParams extends Record<string, any> = Record<string, any>,
> = {
	context: InfiniteLoadingReturn<Data, FilterSearchParams>
	containerStyle?: CSSProperties
	detectionMargin?: string
	/**
	 * By default is false
	 */
	preload?: boolean
	renderComponent?: (props: {
		isLastIncomplete: boolean
		onClick: () => void
	}) => ReactNode
	/**
	 * Determines the nearest scrolling parent (overflow container) by default.
	 * If no `root` is provided, it searches for the closest overflow parent, otherwise, it defaults to the `window`.
	 */
	root?: IntersectionObserverInit['root']
	| MutableRefObject<IntersectionObserverInit['root']>
};

function getOverflowParent(element: HTMLElement | null) {
	while (element && element !== document.body) {
		const overflowY = window.getComputedStyle(element).overflowY;
		const overflowX = window.getComputedStyle(element).overflowX;

		if (
			(
				(overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') && 
				element.scrollHeight > element.clientHeight
			) || (
				(overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'hidden') && 
				element.scrollWidth > element.clientWidth
			)
		) {
			return element;
		}

		element = element.parentElement;
	}

	return null; // No overflow parent found
}

/**
 * Component to help useInfiniteScroll control the scroll
 */
function RefreshControl<
	Data extends any[],
	FilterSearchParams extends Record<string, any> = Record<string, any>,
>({
	context,
	root = null,
	detectionMargin,
	renderComponent,
	preload,
	containerStyle
}: RefreshControlProps<Data, FilterSearchParams>) {
	const ref = useRef<HTMLDivElement | null>(null);

	const contextLoadMore = useEffectEvent(context.loadMore);
	const contextPreload = useEffectEvent(context.preload);

	useEffect(() => {
		if (ref.current) {
			const _root = (
				root &&
				(root as MutableRefObject<IntersectionObserverInit['root']>).current
					? (root as MutableRefObject<IntersectionObserverInit['root']>).current
					: (root as IntersectionObserverInit['root'])
			) ?? getOverflowParent(ref.current);

			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						if (preload) {
							contextPreload();
						}
						else {
							contextLoadMore();
						}
					}
				},
				{
					root: _root,
					rootMargin: detectionMargin,
					threshold: 0
				}
			);

			observer.observe(ref.current);

			return () => {
				observer.disconnect();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detectionMargin, context.data.length]);

	return (
		<div
			ref={ref}
			style={{
				...containerStyle,
				// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
				height: containerStyle?.height || '10px'
			}}
		>
			{
				renderComponent &&
				renderComponent({
					isLastIncomplete: context.isLastIncomplete,
					onClick: () => {
						contextLoadMore();
					}
				})
			}
		</div>
	);
}

export default RefreshControl;
